/**
 * Inspector Rail Component
 *
 * Container for Algebra and Observables inspector panels.
 * Provides split-panel layout with collapsible sections.
 *
 * @see docs/designs/smart-asset-algebra-visualization.md
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { memo, useState, useCallback } from "react";
import type { ValueFlowsSchema } from "../../../types/valueFlows";
import { SchemaStatusHeader } from "./SchemaStatusHeader";
import { AlgebraInspectorPanel } from "./AlgebraInspectorPanel";
import { ObservablesInspectorPanel } from "./ObservablesInspectorPanel";

interface InspectorRailProps {
  /** Value flows schema */
  schema: ValueFlowsSchema;
  /** Width of the rail in pixels */
  width?: number;
  /** Additional CSS classes */
  className?: string;
  /** Callback when rail is closed */
  onClose?: () => void;
}

/**
 * Collapse/expand icon SVG
 */
const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    className={`w-4 h-4 transition-transform ${expanded ? "" : "-rotate-90"}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Close icon SVG
 */
const CloseIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/**
 * Collapsible section component
 */
const CollapsibleSection = memo(
  ({
    title,
    defaultExpanded = true,
    children,
  }: {
    title: string;
    defaultExpanded?: boolean;
    children: React.ReactNode;
  }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-sa-text
            hover:bg-sa-surface-hover transition-colors border-b border-sa-border"
        >
          <ChevronIcon expanded={expanded} />
          <span>{title}</span>
        </button>
        {expanded && <div className="flex-1 min-h-0 overflow-hidden">{children}</div>}
      </div>
    );
  },
);

CollapsibleSection.displayName = "CollapsibleSection";

/**
 * Inspector Rail
 *
 * Side panel containing schema status, algebra inspector, and observables inspector.
 */
export const InspectorRail = memo(
  ({ schema, width = 320, className = "", onClose }: InspectorRailProps) => {
    const hasAlgebra =
      schema.contract_algebra?.expressions &&
      Object.keys(schema.contract_algebra.expressions).length > 0;
    const hasObservables = schema.observables && Object.keys(schema.observables).length > 0;

    // Track which panel is focused (for proportional sizing)
    const [focusedPanel, setFocusedPanel] = useState<"algebra" | "observables" | null>(null);

    const handleAlgebraFocus = useCallback(() => {
      setFocusedPanel((prev) => (prev === "algebra" ? null : "algebra"));
    }, []);

    const handleObservablesFocus = useCallback(() => {
      setFocusedPanel((prev) => (prev === "observables" ? null : "observables"));
    }, []);

    if (!hasAlgebra && !hasObservables) {
      return null;
    }

    return (
      <div
        className={`flex flex-col bg-sa-surface border-l border-sa-border ${className}`}
        style={{ width }}
      >
        {/* Header with close button */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-sa-border">
          <h2 className="text-sm font-semibold text-sa-text">Inspector</h2>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-sa-text-muted hover:text-sa-text hover:bg-sa-surface-hover rounded transition-colors"
              aria-label="Close inspector"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Schema status */}
        <SchemaStatusHeader schema={schema} />

        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Algebra panel */}
          {hasAlgebra && (
            <div
              className={`flex flex-col border-b border-sa-border overflow-hidden transition-all ${
                focusedPanel === "algebra"
                  ? "flex-[2]"
                  : focusedPanel === "observables"
                    ? "flex-[0.5]"
                    : "flex-1"
              }`}
            >
              <button
                type="button"
                onClick={handleAlgebraFocus}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-sa-text
                  hover:bg-sa-surface-hover transition-colors"
              >
                <ChevronIcon expanded={focusedPanel !== "observables"} />
                <span>Contract Algebra</span>
                <span className="text-xs text-sa-text-muted ml-auto">
                  {Object.keys(schema.contract_algebra?.expressions || {}).length}
                </span>
              </button>
              {focusedPanel !== "observables" && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <AlgebraInspectorPanel
                    contractAlgebra={schema.contract_algebra}
                    className="h-full"
                  />
                </div>
              )}
            </div>
          )}

          {/* Observables panel */}
          {hasObservables && (
            <div
              className={`flex flex-col overflow-hidden transition-all ${
                focusedPanel === "observables"
                  ? "flex-[2]"
                  : focusedPanel === "algebra"
                    ? "flex-[0.5]"
                    : "flex-1"
              }`}
            >
              <button
                type="button"
                onClick={handleObservablesFocus}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-sa-text
                  hover:bg-sa-surface-hover transition-colors"
              >
                <ChevronIcon expanded={focusedPanel !== "algebra"} />
                <span>Observables</span>
                <span className="text-xs text-sa-text-muted ml-auto">
                  {Object.keys(schema.observables || {}).length}
                </span>
              </button>
              {focusedPanel !== "algebra" && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <ObservablesInspectorPanel observables={schema.observables} className="h-full" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

InspectorRail.displayName = "InspectorRail";
